import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import emojiMartData, { type EmojiMartData } from "@emoji-mart/data";
import { useEditorState } from "@tiptap/react";

import { useEditorContext } from "../../editor/EditorContext";
import {
  INACTIVE_SUGGESTION,
  getMentionSuggestionStore,
} from "../../editor/extensions/mentionSuggestion";
import type { MentionKindConfig } from "../../editor/types";
import type { MentionSuggestion, SuggestionItem } from "./types";

const MAX_EMOJI_SUGGESTIONS = 20;
const EMOJI_CHAR = ":";

type EmojiIndexEntry = {
  id: string;
  emoji: string;
  name: string;
  keywords: string[];
};

const emojiIndex: EmojiIndexEntry[] = Object.values(
  (emojiMartData as EmojiMartData).emojis,
).flatMap((entry) => {
  const native = entry.skins[0]?.native;
  if (!native) {
    return [];
  }
  return [
    {
      id: entry.id,
      emoji: native,
      name: entry.name,
      keywords: [entry.id, entry.name, ...(entry.keywords ?? [])],
    },
  ];
});

const noopSubscribe = () => () => {};
const inactiveSnapshot = () => INACTIVE_SUGGESTION;

const rawPriority = (item: unknown): number => {
  if (item && typeof item === "object" && "priority" in item) {
    return Number((item as { priority?: unknown }).priority ?? 0);
  }
  return 0;
};

const buildMentionSuggestions = (
  char: string,
  query: string,
  rawMentions: Record<string, unknown[]>,
  configs: MentionKindConfig[],
  fullMentionLinks: boolean,
): MentionSuggestion[] => {
  const result: MentionSuggestion[] = [];

  for (const config of configs) {
    if (config.char !== char) {
      continue;
    }

    for (const item of rawMentions[config.name] ?? []) {
      const formatted = config.formatMention(item);
      const suggestion: MentionSuggestion = {
        kind: "mention",
        id: formatted.id,
        label: formatted.label,
        description: formatted.description,
        image: formatted.image,
        name: config.name,
        priority: rawPriority(item),
        href: fullMentionLinks
          ? window.location.origin + String(formatted.href ?? "")
          : formatted.href,
      };

      if (config.filter && !config.filter(suggestion, query)) {
        continue;
      }
      result.push(suggestion);
    }
  }

  return result.sort((a, b) => b.priority - a.priority);
};

const buildEmojiSuggestions = (query: string): SuggestionItem[] => {
  if (!query) {
    return [];
  }
  const search = query.toLowerCase();
  const matches: SuggestionItem[] = [];

  for (const entry of emojiIndex) {
    const isMatch = entry.keywords.some((keyword) =>
      keyword.toLowerCase().includes(search),
    );

    if (!isMatch) {
      continue;
    }

    matches.push({
      kind: "emoji",
      id: entry.id,
      emoji: entry.emoji,
      label: `:${entry.id}:`,
      description: entry.name,
    });

    if (matches.length >= MAX_EMOJI_SUGGESTIONS) {
      break;
    }
  }

  return matches;
};

type UseMentionProps = {
  configs: MentionKindConfig[];
  emojiEnabled?: boolean;
  rawMentions: Record<string, unknown[]>;
  onSuggest: (names: string[], query: string) => void;
  onMentionSelect?: (mention: MentionSuggestion) => void;
  fullMentionLinks?: boolean;
};

/*
The suggestion plugins inside the editor publish the active trigger and query, this hook turns that
into the list of matches the dropdown renders and handles the keyboard interaction the plugin hands
back to us.
*/
const useMention = ({
  configs,
  emojiEnabled = false,
  rawMentions,
  onSuggest,
  onMentionSelect,
  fullMentionLinks = false,
}: UseMentionProps) => {
  const { editor } = useEditorContext();
  const isFocused =
    useEditorState({
      editor,
      selector: ({ editor: instance }) => !!instance?.isFocused,
    }) ?? false;

  const store = getMentionSuggestionStore(editor);
  const suggestionState = useSyncExternalStore(
    store?.subscribe ?? noopSubscribe,
    store?.getState ?? inactiveSnapshot,
  );

  const { active, char, query } = suggestionState;
  const isEmoji = emojiEnabled && char === EMOJI_CHAR;
  const sessionKey = `${active}\0${char}\0${query}`;
  const fetchKey = active && !isEmoji ? `${char}\0${query}` : null;

  const [activeIndex, setActiveIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [pendingFetchKey, setPendingFetchKey] = useState<string | null>(
    fetchKey,
  );

  // Reset selection / Escape-dismiss when the active suggestion session changes.
  // Mark loading immediately for entity fetches so we don't wait for the effect.
  const [seenSessionKey, setSeenSessionKey] = useState(sessionKey);
  if (seenSessionKey !== sessionKey) {
    setSeenSessionKey(sessionKey);
    setActiveIndex(0);
    setDismissed(false);
    setPendingFetchKey(fetchKey);
  }

  // Results arriving clears the in-flight fetch marker.
  const [seenMentions, setSeenMentions] = useState(rawMentions);
  if (seenMentions !== rawMentions) {
    setSeenMentions(rawMentions);
    setPendingFetchKey(null);
  }

  const namesForChar = useMemo(() => {
    const names: Record<string, string[]> = {};
    for (const config of configs) {
      const existing = names[config.char];
      if (existing) {
        existing.push(config.name);
      } else {
        names[config.char] = [config.name];
      }
    }
    return names;
  }, [configs]);

  useEffect(() => {
    if (!fetchKey) {
      return;
    }
    const names = namesForChar[char];
    if (!names?.length) {
      setPendingFetchKey(null);
      return;
    }
    onSuggest(names, query);
  }, [fetchKey, char, query, namesForChar, onSuggest]);

  const suggestions = useMemo((): SuggestionItem[] => {
    if (!active) {
      return [];
    }
    if (isEmoji) {
      return buildEmojiSuggestions(query);
    }
    return buildMentionSuggestions(
      char,
      query,
      rawMentions,
      configs,
      fullMentionLinks,
    );
  }, [active, isEmoji, char, query, rawMentions, configs, fullMentionLinks]);

  const createMention = useCallback(
    (index: number) => {
      const suggestion = suggestions[index];
      if (!suggestion || !suggestionState.command) {
        return;
      }

      if (suggestion.kind === "emoji") {
        suggestionState.command({ emoji: suggestion.emoji });
        return;
      }

      onMentionSelect?.(suggestion);
      suggestionState.command({
        id: suggestion.id,
        label: suggestion.label,
        name: suggestion.name,
        href: suggestion.href,
      });
    },
    [suggestions, suggestionState, onMentionSelect],
  );

  // The suggestion plugin owns the keydown events while it is active, so it forwards them here
  // through a handler that has to stay stable.
  const handlerState = useRef({ suggestions, activeIndex, createMention });
  handlerState.current = { suggestions, activeIndex, createMention };

  useEffect(() => {
    if (!store) {
      return;
    }

    store.setKeyHandler((event) => {
      if (event.key === "Escape") {
        setDismissed(true);
        return true;
      }

      const count = handlerState.current.suggestions.length;
      if (count === 0) {
        return false;
      }

      switch (event.key) {
        case "ArrowDown":
          setActiveIndex((index) => (index + 1) % count);
          return true;
        case "ArrowUp":
          setActiveIndex((index) => (index - 1 + count) % count);
          return true;
        case "Enter":
        case "Tab":
          handlerState.current.createMention(handlerState.current.activeIndex);
          return true;
        default:
          return false;
      }
    });

    return () => {
      store.setKeyHandler(null);
    };
  }, [store]);

  return {
    suggestions,
    activeIndex,
    setActiveIndex,
    createMention,
    isActive: active && !dismissed && isFocused,
    loading: pendingFetchKey !== null && pendingFetchKey === fetchKey,
    clientRect: suggestionState.clientRect,
  };
};

export default useMention;
